import { useState, useCallback } from 'react';
import { 
  OtpRequestPayloadI, 
  OtpValidatePayloadI, 
  OtpResendPayloadI, 
  OtpStateI 
} from "../../interfaces";
import { otpRequestNoAuth, otpValidateNoAuth, otpResendNoAuth } from "../../apis";

// Estado inicial del OTP
const initialOtpState: OtpStateI = {
  id: null,
  expiresAt: null,
  attemptsLeft: 0,
  contactMethod: null,
  maskedContact: null,
  otpToken: null,
  isRequesting: false,
  isValidating: false,
  isResending: false,
  error: null,
};

export const useOtpValidation = () => {
  const [otpState, setOtpState] = useState<OtpStateI>(initialOtpState);

  // Función para solicitar OTP
  const requestOtp = useCallback(async (payload: OtpRequestPayloadI): Promise<boolean> => {
    setOtpState(prev => ({ 
      ...prev, 
      isRequesting: true, 
      error: null 
    }));
    
    try {
      const { data: response } = await otpRequestNoAuth(payload);
      
      if (response) {
        setOtpState(prev => ({
          ...prev,
          id: response.otpId,
          expiresAt: new Date(response.expiresAt),
          attemptsLeft: response.attemptsLeft,
          contactMethod: response.contactMethod,
          maskedContact: response.maskedContact,
          isRequesting: false,
          error: null,
        }));
        return true;
      }
      
      throw new Error('No se recibió respuesta del servidor');
    } catch (err: any) {
      setOtpState(prev => ({
        ...prev,
        isRequesting: false,
        error: err.message || 'Error al solicitar OTP',
      }));
      throw err;
    }
  }, []);

  // 🔧 FUNCIÓN CORREGIDA - Retorna tanto el estado como el token
  const validateOtp = useCallback(async (otpCode: string): Promise<{
    isValid: boolean;
    token?: string;
  }> => {
    if (!otpState.id) {
      throw new Error('No hay solicitud de OTP activa');
    }

    setOtpState(prev => ({ 
      ...prev, 
      isValidating: true, 
      error: null 
    }));
    
    try {
      const payload: OtpValidatePayloadI = {
        otpId: otpState.id,
        otpCode: otpCode.trim()
      };

      const { data: response } = await otpValidateNoAuth(payload);
      console.log('Response del servidor:', response);
      
      if (response?.isValid) {
        // Actualizar el estado inmediatamente
        setOtpState(prev => ({
          ...prev,
          isValidating: false,
          otpToken: response.otpToken || null,
          error: null,
        }));
        
        console.log('Token OTP obtenido:', response.otpToken);
        
        // Retornar tanto el estado como el token para uso inmediato
        return {
          isValid: true,
          token: response.otpToken
        };
      } else {
        // Actualizar intentos restantes si vienen en la respuesta
        setOtpState(prev => ({
          ...prev,
          isValidating: false,
          attemptsLeft: response?.attemptsLeft ?? prev.attemptsLeft,
          error: response?.message || 'Código OTP inválido',
        }));
        
        return {
          isValid: false
        };
      }
    } catch (err: any) {
      setOtpState(prev => ({
        ...prev,
        isValidating: false,
        error: err.message || 'Error al validar OTP',
      }));
      throw err;
    }
  }, [otpState.id]);

  // Función para reenviar OTP
  const resendOtp = useCallback(async (): Promise<boolean> => {
    if (!otpState.id) {
      throw new Error('No hay solicitud de OTP para reenviar');
    }

    setOtpState(prev => ({ 
      ...prev, 
      isResending: true, 
      error: null 
    }));
    
    try {
      const payload: OtpResendPayloadI = {
        otpId: otpState.id
      };

      const { data: response } = await otpResendNoAuth(payload);
      
      if (response) {
        setOtpState(prev => ({
          ...prev,
          expiresAt: new Date(response.expiresAt),
          attemptsLeft: response.attemptsLeft,
          maskedContact: response.maskedContact,
          isResending: false,
          error: null,
        }));
        return true;
      }
      
      throw new Error('No se pudo reenviar el OTP');
    } catch (err: any) {
      setOtpState(prev => ({
        ...prev,
        isResending: false,
        error: err.message || 'Error al reenviar OTP',
      }));
      throw err;
    }
  }, [otpState.id]);

  // Función para resetear el estado OTP
  const resetOtp = useCallback(() => {
    setOtpState(initialOtpState);
  }, []);

  // Función para limpiar errores
  const clearError = useCallback(() => {
    setOtpState(prev => ({ ...prev, error: null }));
  }, []);

  // Helpers computados
  const isExpired = otpState.expiresAt ? new Date() > otpState.expiresAt : false;
  const hasAttemptsLeft = otpState.attemptsLeft > 0;
  const isLoading = otpState.isRequesting || otpState.isValidating || otpState.isResending;
  const isValidated = !!otpState.otpToken;
  const canResend = otpState.id && !otpState.isResending && hasAttemptsLeft;

  return {
    // Estado
    otpState,
    
    // Estados computados
    isExpired,
    hasAttemptsLeft,
    isLoading,
    isValidated,
    canResend,
    
    // Acciones
    requestOtp,
    validateOtp,
    resendOtp,
    resetOtp,
    clearError,
    
    // Estados específicos para UI
    isRequesting: otpState.isRequesting,
    isValidating: otpState.isValidating,
    isResending: otpState.isResending,
    error: otpState.error,
    otpToken: otpState.otpToken,
  };
};